'use client'

import React, { useState, useRef, useEffect } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'

type Props = {
  categories: readonly { label: string; value: string }[]
  baseUrl: string
}

export function CategoryFilter({ categories, baseUrl }: Props) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const categoryParam = searchParams.get('category') || ''
  const activeCategories = categoryParam ? categoryParam.split(',') : []

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleToggle = (value: string) => {
    const next = activeCategories.includes(value)
      ? activeCategories.filter((v) => v !== value)
      : [...activeCategories, value]

    const params = new URLSearchParams()
    if (next.length > 0) {
      params.set('category', next.join(','))
    }
    const url = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl
    router.push(url, { scroll: false })
  }

  const handleReset = () => {
    router.push(baseUrl, { scroll: false })
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-2 rounded-[8px] border border-civo-cloud px-4 py-2 text-sm font-medium transition-colors hover:bg-black/5"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          className="shrink-0"
        >
          <path
            d="M2 4H14M4 8H12M6 12H10"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
        Filters
        {activeCategories.length > 0 && (
          <span className="size-5 rounded-full bg-black text-white text-xs flex items-center justify-center">
            {activeCategories.length}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 z-50 w-[198px] rounded-[4px] border border-civo-cloud bg-white shadow-lg">
          <div className="p-3 flex flex-col gap-2 max-h-[148px] overflow-y-auto">
            {categories.map((cat) => (
              <label key={cat.value} className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={activeCategories.includes(cat.value)}
                  onChange={() => handleToggle(cat.value)}
                  className="size-4 rounded border-gray-300"
                />
                {cat.label}
              </label>
            ))}
          </div>
          {activeCategories.length > 0 && (
            <div className="border-t border-civo-cloud p-3">
              <button
                onClick={handleReset}
                className="text-sm text-black/70 hover:text-black transition-colors"
              >
                Reset
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
